/**
 * Evaluation Service
 * 
 * This service handles all evaluation-related API calls.
 * It uses the privateApiClient for authenticated requests.
 */

import { privateApiClient, handleApiError } from '../apiClient';
import API from '../../config/api';

/**
 * Get an evaluation for a nomination
 * @param {string|number} nominationId - Nomination ID
 * @returns {Promise<Object>} Object with data property containing evaluation details
 */
export const getEvaluationByNominationId = async (nominationId) => {
  try {
    // Backend returns a list of evaluations for a nomination
    const response = await privateApiClient.get(
      API.committee.evaluations.getByNomination(nominationId)
    );
    const list = Array.isArray(response.data) ? response.data : [];
    // Return the first evaluation if exists, otherwise null
    return { data: list.length > 0 ? list[0] : null };
  } catch (error) {
    // If 404, it means no evaluation exists yet, which is not an error
    if (error.response && error.response.status === 404) {
      return { data: null };
    }
    return handleApiError(error, `Failed to fetch evaluation for nomination ID: ${nominationId}`);
  }
};

/**
 * Create a new evaluation
 * @param {Object} evaluationData - Evaluation data
 * @returns {Promise<Object>} Object with data property containing created evaluation
 */
export const createEvaluation = async (evaluationData) => {
  try {
    const response = await privateApiClient.post(API.committee.evaluations.create, evaluationData);
    return { data: response.data };
  } catch (error) {
    return handleApiError(error, 'Failed to create evaluation');
  }
};

/**
 * Update an existing evaluation
 * @param {string|number} evaluationId - Evaluation ID
 * @param {Object} evaluationData - Updated evaluation data
 * @returns {Promise<Object>} Object with data property containing updated evaluation
 */
export const updateEvaluation = async (evaluationId, evaluationData) => {
  try {
    const response = await privateApiClient.put(`${API.committee.evaluations.update}/${evaluationId}`, evaluationData);
    return { data: response.data };
  } catch (error) {
    return handleApiError(error, `Failed to update evaluation with ID: ${evaluationId}`);
  }
};

/**
 * Submit a final evaluation
 * @param {string|number} evaluationId - Evaluation ID
 * @param {Object} evaluationData - Evaluation data with status set to 'submitted'
 * @returns {Promise<Object>} Object with data property containing submitted evaluation
 */
export const submitEvaluation = async (evaluationId, evaluationData) => {
  try {
    // Ensure status is set to submitted
    const data = { ...evaluationData, status: 'submitted' };
    const response = await privateApiClient.put(`${API.committee.evaluations.update}/${evaluationId}`, data);
    return { data: response.data };
  } catch (error) {
    return handleApiError(error, `Failed to submit evaluation with ID: ${evaluationId}`);
  }
};

/**
 * Get all evaluations for a committee member
 * @param {Object} filters - Optional filters
 * @returns {Promise<Object>} Object with data property containing list of evaluations
 */
export const getMyEvaluations = async (filters = {}) => {
  try {
    // Supported server-side params in backend: skip, limit, nomination_id
    const params = {};
    if (typeof filters.skip === 'number') params.skip = filters.skip;
    if (typeof filters.limit === 'number') params.limit = filters.limit;
    if (filters.nominationId) params.nomination_id = filters.nominationId;

    const response = await privateApiClient.get(API.evaluations.base, { params });
    return { data: Array.isArray(response.data) ? response.data : [] };
  } catch (error) {
    return handleApiError(error, 'Failed to fetch evaluations');
  }
};

// Export all functions as a single object
const evaluationService = {
  getEvaluationByNominationId,
  createEvaluation,
  updateEvaluation,
  submitEvaluation,
  getMyEvaluations
};

export default evaluationService;
